import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiJson } from "@/api/client.js";
import { AvenzaActions } from "@/components/AvenzaActions.jsx";
import { OfficialLogo } from "@/components/OfficialLogo.jsx";
import { TerritoryMap, MAP_LAYERS, POINT_COLORS } from "@/components/TerritoryMap.jsx";
import { EXPEDITION } from "@/content/expedition.js";
import { STRATEGIC_CATEGORIES } from "@/config/orignalAppConfig.js";
import { useAuth } from "@/context/AuthContext.jsx";
import { useMarcelMode } from "@/context/MarcelMode.jsx";
import { useOrignalUi } from "@/hooks/useOrignalUi.js";
import { listQueue } from "@/lib/offlineQueue.js";

const INTEREST = [
  { id: 1, key: "normal" },
  { id: 2, key: "interesting" },
  { id: 3, key: "important" },
  { id: 4, key: "priority" },
];

export function MapFullPage() {
  const { ui } = useOrignalUi();
  const { user } = useAuth();
  const { actingAsAdmin } = useMarcelMode();
  const mapApi = useRef(null);
  const [points, setPoints] = useState([]);
  const [pending, setPending] = useState([]);
  const [selected, setSelected] = useState(null);
  const [layer, setLayer] = useState("standard");
  const [sheet, setSheet] = useState("");
  const [placeMode, setPlaceMode] = useState(false);
  const [gps, setGps] = useState({ msg: "", state: "idle", acc: null });
  const [members, setMembers] = useState([]);
  const [filters, setFilters] = useState({ view: "all", member: "", day: "", layerOn: { secteur: true, points: true, revisit: false } });
  const [draft, setDraft] = useState({ category: "traces", comment: "", interest: 2, revisit: false, lat: null, lng: null });
  const [limit, setLimit] = useState(null);
  const [tracks, setTracks] = useState([]);
  const [fabOpen, setFabOpen] = useState(false);

  async function reload() {
    try {
      const p = await api("/api/territory/points");
      setPoints(p.items || []);
    } catch {
      /* offline — keep last known server points */
    }
    try {
      const q = await listQueue();
      setPending((q || []).filter((x) => x.kind === "point").map(queuePoint));
    } catch {
      setPending([]);
    }
  }
  useEffect(() => {
    reload().catch(() => {});
    api("/api/territory/secteur4").then(setLimit).catch(() => {});
    api("/api/territory/tracks").then((d) => setTracks(d.items || [])).catch(() => {});
    api("/api/members").then((d) => setMembers((d.items || []).filter((m) => (m.kind || "official") === "official"))).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(() => mapApi.current?.invalidate(), 200);
    return () => clearTimeout(t);
  }, [sheet, selected]);

  useEffect(() => {
    const onResize = () => setTimeout(() => mapApi.current?.invalidate(), 250);
    window.addEventListener("orientationchange", onResize);
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("orientationchange", onResize);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  const names = Object.fromEntries(members.map((m) => [m.id, m.display_name || m.first_name]));
  const mineId = user?.member?.id || user?.member_id;
  const merged = [...points, ...pending.filter((p) => !points.some((x) => x.client_id && x.client_id === p.client_id))];
  const visible = useMemo(() => {
    return merged.filter((p) => {
      if (!filters.layerOn.points) return false;
      if (filters.layerOn.revisit && !p.revisit) return false;
      if (filters.view === "mine" && p.author_id !== mineId) return false;
      if (filters.view === "others" && p.author_id === mineId) return false;
      if (filters.view === "priority" && Number(p.interest) < 4) return false;
      if (filters.view === "revisit" && !p.revisit) return false;
      if (filters.view === "acces" && !["acces", "stationnement", "chemin"].includes(p.category)) return false;
      if (filters.view === "obs" && !["orignal", "traces", "frottage", "souille", "observation"].includes(p.category)) return false;
      if (filters.member && p.author_id !== filters.member) return false;
      if (filters.day && (p.observed_at || p.created_at || "").slice(0, 10) !== filters.day) return false;
      return true;
    });
  }, [merged, filters, mineId]);

  function useGps(thenPlace) {
    if (!navigator.geolocation) {
      setGps({ msg: ui.mapUi.noGps, state: "denied", acc: null });
      return;
    }
    setGps({ msg: ui.mapUi.locating, state: "search", acc: null });
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        setGps({ msg: `${lat.toFixed(5)}, ${lng.toFixed(5)}`, state: "ok", acc: pos.coords.accuracy });
        mapApi.current?.flyTo(lat, lng, 15);
        if (thenPlace) setDraft((d) => ({ ...d, lat, lng }));
      },
      (err) => {
        setGps({ msg: err.code === 1 ? ui.mapUi.denied : ui.mapUi.noGps, state: "denied", acc: null });
      },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  }

  async function savePoint() {
    if (!actingAsAdmin) return;
    if (draft.lat == null || draft.lng == null) return;
    const cat = ui.categories[draft.category] || draft.category;
    const saved = {
      name: cat,
      category: draft.category,
      latitude: draft.lat,
      longitude: draft.lng,
      comment: draft.comment,
      interest: draft.interest,
      revisit: draft.revisit,
      trip_id: "trip_terrain_3j",
    };
    const res = await apiJson("/api/territory/points", saved);
    setPlaceMode(false);
    setDraft({ category: "traces", comment: "", interest: 2, revisit: false, lat: null, lng: null });
    const local = {
      ...saved,
      ...res,
      id: res.id || res.client_id || `local-${Date.now()}`,
      sync: res._offline ? "pending" : "ok",
    };
    if (res._offline) setPending((p) => [...p, local]);
    setSelected(local);
    setSheet("detail");
    await reload();
  }

  async function patchSelected(body) {
    if (!selected?.id || String(selected.id).startsWith("local")) return;
    const res = await apiJson(`/api/territory/points/${selected.id}`, body, "PATCH");
    setSelected(res);
    await reload();
  }

  const canPlaceRef = useRef(false);
  canPlaceRef.current = placeMode || sheet === "add";
  const seedPlace = useCallback(() => {
    const c = mapApi.current?.getCenter?.();
    const lat = c?.lat ?? EXPEDITION.mapCenter.lat;
    const lng = c?.lng ?? EXPEDITION.mapCenter.lng;
    setDraft((d) => (d.lat == null ? { ...d, lat, lng } : d));
  }, []);
  const onAddAt = useCallback((coords) => {
    if (!canPlaceRef.current) return;
    setDraft((d) => ({ ...d, lat: coords.latitude, lng: coords.longitude }));
    setSheet("add");
    setPlaceMode(false);
  }, []);

  const canEdit = selected && (actingAsAdmin || selected.author_id === mineId);

  return (
    <div className="og-map-page" data-testid="page-territory">
      <div className="og-map-page__stage" data-testid="page-map-full">
      <TerritoryMap
        ref={mapApi}
        full
        points={visible}
        layer={layer}
        selectedId={selected?.id}
        onSelect={(p) => { setSelected(p); setSheet("detail"); }}
        onAddAt={onAddAt}
        testId="territory-map-full"
        limit={limit}
        tracks={tracks}
        placeMode={placeMode || sheet === "add"}
      />
      <div className="og-map-tools" data-testid="map-toolbar">
        <OfficialLogo size="page" decorative />
        <button type="button" className="og-btn" onClick={() => useGps(false)} data-testid="use-gps">{ui.mapUi.myPos}</button>
        {actingAsAdmin ? (
          <button type="button" className="og-btn og-btn--primary" data-testid="add-strategic" onClick={() => { setSheet("add"); setPlaceMode(false); seedPlace(); }}>+ {ui.mapUi.strategic}</button>
        ) : (
          <p className="og-map-note" data-testid="nordquest-points-note">
            {ui.nordquest.mapNote} <Link to="/preparation/nordquest">{ui.nordquest.open}</Link>
          </p>
        )}
        <button type="button" className="og-btn" data-testid="fit-sector" onClick={() => mapApi.current?.fitSector()}>{ui.mapUi.fitSector}</button>
        <button type="button" className="og-btn" data-testid="open-filters" onClick={() => setSheet(sheet === "filters" ? "" : "filters")}>{ui.mapUi.filters}</button>
        <button type="button" className="og-btn" data-testid="open-layers" onClick={() => setSheet(sheet === "layers" ? "" : "layers")}>{ui.mapUi.layers}</button>
        <Link className="og-btn" to="/preparation/nordquest" data-testid="open-nordquest">{ui.nordquest.open}</Link>
        <div className="og-map-tools__extra">
          <AvenzaActions lat={selected?.latitude || EXPEDITION.mapCenter.lat} lng={selected?.longitude || EXPEDITION.mapCenter.lng} ui={ui} compact />
        </div>
      </div>
      <p className="og-map-badge" data-testid="sector-badge">
        <span data-testid="prospect-points-label">{ui.hunt.prospectPoints}</span>
        {" · Secteur 4 — 54,55 km²"}
      </p>
      <p className="og-map-badge og-map-badge--lake" data-testid="lake-badge">{ui.avenza.lakeTitle}</p>
      <div className="og-map-fab" data-testid="map-fab">
        <button type="button" className="og-fab" onClick={() => setFabOpen((v) => !v)}>+</button>
        {fabOpen ? (
          <div className="og-fab-menu">
            {actingAsAdmin ? <button type="button" onClick={() => { setFabOpen(false); setSheet("add"); seedPlace(); }}>{ui.mapUi.strategic}</button> : null}
            <Link to="/observations?new=1">{ui.today.observe}</Link>
            <Link to="/photos?new=1">{ui.today.photo}</Link>
          </div>
        ) : null}
      </div>
      {gps.msg ? <p className="og-map-gps" data-testid="gps-state">{gps.msg}{gps.acc ? ` · ±${Math.round(gps.acc)} m` : ""}</p> : null}

      {sheet === "filters" ? (
        <aside className="og-map-sheet" data-testid="map-filters">
          <button type="button" className="og-btn og-map-sheet__close" data-testid="map-sheet-close" onClick={() => setSheet("")}>{ui.app.close}</button>
          <h3>{ui.mapUi.show}</h3>
          {["all", "mine", "others", "priority", "revisit", "obs", "acces"].map((id) => (
            <button key={id} type="button" className={`og-chip${filters.view === id ? " is-active" : ""}`} onClick={() => setFilters({ ...filters, view: id })}>{ui.mapUi.views[id]}</button>
          ))}
          <select className="og-input" value={filters.member} onChange={(e) => setFilters({ ...filters, member: e.target.value })}>
            <option value="">{ui.mapUi.allMembers}</option>
            {members.map((m) => <option key={m.id} value={m.id}>{m.first_name}</option>)}
          </select>
          <div className="og-chips">
            {["2026-08-28", "2026-08-29", "2026-08-30"].map((d) => (
              <button key={d} type="button" className={`og-chip${filters.day === d ? " is-active" : ""}`} onClick={() => setFilters({ ...filters, day: filters.day === d ? "" : d })}>{d.slice(8)} août</button>
            ))}
          </div>
        </aside>
      ) : null}

      {sheet === "layers" ? (
        <aside className="og-map-sheet" data-testid="map-layers">
          <button type="button" className="og-btn og-map-sheet__close" onClick={() => setSheet("")}>{ui.app.close}</button>
          {MAP_LAYERS.map((id) => (
            <button key={id} type="button" className={`og-chip${layer === id ? " is-active" : ""}`} data-testid={`layer-${id}`} onClick={() => setLayer(id)}>{ui.territory.layers[id]}</button>
          ))}
        </aside>
      ) : null}

      {sheet === "add" ? (
        <aside className="og-map-sheet" data-testid="point-compose">
          <button type="button" className="og-btn og-map-sheet__close" onClick={() => setSheet("")}>{ui.app.close}</button>
          <h3>{ui.mapUi.strategic}</h3>
          <div className="og-toolbar">
            <button type="button" className="og-btn og-btn--primary" data-testid="place-gps" onClick={() => useGps(true)}>{ui.mapUi.useHere}</button>
            <button type="button" className={`og-btn${placeMode ? " og-btn--primary" : ""}`} data-testid="place-map" onClick={() => {
              setPlaceMode(true);
              seedPlace();
            }}>{ui.mapUi.tapMap}</button>
          </div>
          {draft.lat != null ? <p className="og-hint">{draft.lat.toFixed(5)}, {draft.lng.toFixed(5)}</p> : <p className="og-hint">{ui.mapUi.needPlace}</p>}
          <select className="og-input" value={draft.category} onChange={(e) => setDraft({ ...draft, category: e.target.value })} data-testid="point-cat">
            {STRATEGIC_CATEGORIES.map((c) => <option key={c} value={c}>{ui.categories[c] || c}</option>)}
          </select>
          <textarea className="og-input" value={draft.comment} onChange={(e) => setDraft({ ...draft, comment: e.target.value })} data-testid="point-comment" placeholder={ui.territory.comment} />
          <select className="og-input" value={draft.interest} onChange={(e) => setDraft({ ...draft, interest: Number(e.target.value) })}>
            {INTEREST.map((i) => <option key={i.id} value={i.id}>{ui.mapUi.interest[i.key]}</option>)}
          </select>
          <label className="og-check"><input type="checkbox" checked={draft.revisit} onChange={(e) => setDraft({ ...draft, revisit: e.target.checked })} data-testid="point-revisit" /> {ui.mapUi.revisit}</label>
          <button type="button" className="og-btn og-btn--primary" data-testid="point-save" disabled={draft.lat == null} onClick={savePoint}>{ui.app.save}</button>
        </aside>
      ) : null}

      {sheet === "detail" && selected ? (
        <aside className="og-map-sheet" data-testid="point-detail">
          <button type="button" className="og-btn og-map-sheet__close" onClick={() => { setSheet(""); setSelected(null); }}>{ui.app.close}</button>
          <h3>{ui.categories[selected.category] || selected.name}</h3>
          <p>{names[selected.author_id] || selected.author_id} · {(selected.created_at || "").replace("T", " ").slice(0, 16)}</p>
          <p>{selected.latitude?.toFixed?.(5)}, {selected.longitude?.toFixed?.(5)}</p>
          <p>{selected.comment}</p>
          {selected.sync === "pending" ? <p className="og-crit">{ui.app.toSync}</p> : null}
          <AvenzaActions lat={selected.latitude} lng={selected.longitude} ui={ui} />
          {canEdit ? (
            <div className="og-toolbar">
              <button type="button" className="og-btn" data-testid="mark-revisit" onClick={() => patchSelected({ revisit: !selected.revisit })}>⭐ {ui.mapUi.revisit}</button>
              <button type="button" className="og-btn og-btn--ghost" onClick={() => patchSelected({ archive: true }).then(() => { setSelected(null); setSheet(""); })}>{ui.app.archive}</button>
            </div>
          ) : null}
        </aside>
      ) : null}

      <aside className="og-map-list" data-testid="point-list">
        {visible.map((p) => (
          <button
            key={p.id}
            type="button"
            className={`og-map-list__row${selected?.id === p.id ? " is-active" : ""}`}
            data-testid={`point-row-${p.id}`}
            onClick={() => { setSelected(p); setSheet("detail"); mapApi.current?.flyTo(p.latitude, p.longitude, 14); }}
          >
            <i style={{ background: POINT_COLORS[p.category] || "#7A6B5A" }} />
            <span>{p.comment || ui.categories[p.category] || p.name}</span>
            <small>{names[p.author_id] || ""} · {(p.created_at || "").slice(0, 10)}</small>
          </button>
        ))}
      </aside>
      <div className="og-map-legend" data-testid="map-legend">
        {["traces", "orignal", "acces", "eau", "revoir"].map((c) => (
          <span key={c}><i style={{ background: POINT_COLORS[c] }} />{ui.categories[c]}</span>
        ))}
      </div>
      </div>
    </div>
  );
}

function queuePoint(row) {
  const body = row.payload || row.body || {};
  return {
    id: row.client_id,
    client_id: row.client_id,
    name: body.name,
    category: body.category,
    latitude: body.latitude,
    longitude: body.longitude,
    comment: body.comment,
    interest: body.interest,
    revisit: body.revisit,
    sync: "pending",
    author_id: "local",
  };
}
